import { FormikProps } from 'formik';
import React, { useEffect, useState } from 'react';

import { TAccountGQLType } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { BankAccountWithInstitutionAndOwnerFragmentGQLType } from 'packages/bankAccounts/requests/bankAccounts.gql.generated';
import { CreditAgreementFullArtifact } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/CreditAgreementFullArtifact';
import { useWipConfig } from '~/packages/creditAgreements/ui/creditAgreementWip/hooks/useWipConfig';
import { wipExitAlertCallback } from '~/packages/creditAgreements/ui/creditAgreementWip/utils/wipAlertCallback';
import {
  CreditAgreement,
  DbTable,
  LoanProductsMap,
  OrganizationMap,
  CreditAgreementFullArtifactWipConfigType,
} from '~/packages/creditAgreements/ui/types';

export type CreditAgreementFullArtifactWithIntrospectionEntityFetchProps = {
  organizationId: string;
  creditAgreementFullArtifact: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  organizationMap: OrganizationMap;
  tAccounts: TAccountGQLType[];
  facilityType: DbTable.FacilityType;
  formProps: FormikProps<CreditAgreement.DomainEntity.CreditAgreementFullArtifact>;
  creditFacilityId: string;
  implementationId: string;
  arePartiesLocked: boolean;
  setArePartiesLocked: React.Dispatch<React.SetStateAction<boolean>>;
  loanProductsMap: LoanProductsMap;
  operationMode: CreditAgreement.OperationMode;
  // Optional props for customization
  saveCreditAgreementFullArtifactWip: CreditAgreementFullArtifactWipConfigType['saveCreditAgreementFullArtifactWip'];
  isSaveCreditAgreementFullArtifactWipLoading?: boolean;
  saveAndUpdateConfigCreditAgreementWip: CreditAgreementFullArtifactWipConfigType['saveAndUpdateConfigCreditAgreementWip'];
  children?: React.ReactNode;
  bankAccountsWithInstitutionAndOwner: BankAccountWithInstitutionAndOwnerFragmentGQLType[];
};

/**
 * Reusable introspection entity fetch component that parallels "CreditAgreementFullArtifactWithDynamicEntityFetch"
 * on admin dashboard, but instantiates wip config interface with information
 * already existing within the CA wip, as lender cannot have full access to all
 * organizations, users, and bank accounts
 */
export const CreditAgreementFullArtifactWithIntrospectionEntityFetch = ({
  organizationId,
  creditAgreementFullArtifact,
  organizationMap,
  tAccounts,
  facilityType,
  formProps,
  creditFacilityId,
  bankAccountsWithInstitutionAndOwner,
  arePartiesLocked,
  setArePartiesLocked,
  loanProductsMap,
  operationMode,
  saveCreditAgreementFullArtifactWip,
  isSaveCreditAgreementFullArtifactWipLoading = false,
  saveAndUpdateConfigCreditAgreementWip,
  children,
}: CreditAgreementFullArtifactWithIntrospectionEntityFetchProps) => {
  const [isFormDirty, setIsFormDirty] = useState(formProps.dirty);

  useEffect(() => {
    if (isFormDirty !== formProps.dirty) {
      setIsFormDirty(formProps.dirty);
    }
  }, [formProps, isFormDirty]);

  // Display browser warning popup when user attempts to manually refresh or close page
  useEffect(() => {
    if (isFormDirty) {
      window.addEventListener('beforeunload', wipExitAlertCallback);
    }
    // remove exit prompt event listener when unmounting
    return () => {
      window.removeEventListener('beforeunload', wipExitAlertCallback);
    };
  }, [isFormDirty]);

  // Use the reusable hook for wipConfig creation
  // For introspection, we don't pass orgProvidedInputKeys so it extracts from WIP
  const { wipConfig } = useWipConfig({
    creditAgreementFullArtifact,
    formProps,
    organizationMap,
    tAccounts,
    loanProductsMap,
    facilityType,
    creditFacilityId,
    arePartiesLocked,
    setArePartiesLocked,
    operationMode,
    saveCreditAgreementFullArtifactWip,
    isSaveCreditAgreementFullArtifactWipLoading,
    saveAndUpdateConfigCreditAgreementWip,
    bankAccountsWithInstitutionAndOwner,

    // Don't pass orgProvidedInputKeys, let it extract from WIP for introspection
  });

  // If children are provided, render them with the wipConfig
  if (children) {
    return <>{children}</>;
  }

  // Default to rendering CreditAgreementFullArtifact
  return (
    <CreditAgreementFullArtifact
      organizationId={organizationId}
      creditAgreementFullArtifact={creditAgreementFullArtifact}
      wipConfig={wipConfig}
    />
  );
};
